// HR > Onboarding Checklist (Phase 1). List of hires with joining-checklist
// progress, and a detail view: 30 items grouped by category, each a toggle + note.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { HRC, fmtDate, Card, SectionTitle, Th, Td, Table, Pill, Btn, Stat, input, Modal, Empty, Loading } from './hrkit';
import { reportError } from '../../utils/reportError';

const CATS = [
  { key: 'documents', label: 'Documents' },
  { key: 'forms', label: 'Forms' },
  { key: 'letters', label: 'Letters' },
  { key: 'it_assets', label: 'IT Assets' },
  { key: 'milestones', label: 'Milestones' },
];
const catLabel = (k) => (CATS.find(c => c.key === k) || {}).label || k;

function Progress({ done, total, width = 140 }) {
  const t = total || 0, d = done || 0;
  const pct = t ? Math.round((d / t) * 100) : 0;
  const color = pct === 100 ? HRC.green : pct >= 50 ? HRC.navy : HRC.amber;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width, height: 8, background: '#ECEBF3', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 6, transition: 'width .2s' }} />
      </div>
      <span style={{ fontSize: 12, color: HRC.muted, whiteSpace: 'nowrap', fontWeight: 600 }}>{d}/{t} · {pct}%</span>
    </div>
  );
}

export default function Checklists({ target, clearTarget }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailEmp, setDetailEmp] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/hr/checklists').then(r => setRows(r.data.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-open the detail when navigated here straight after a hire.
  useEffect(() => {
    if (target) { setDetailEmp(target); clearTarget?.(); }
  }, [target, clearTarget]);

  const kpi = {
    total: rows.length,
    inProgress: rows.filter(r => r.status !== 'complete').length,
    complete: rows.filter(r => r.status === 'complete').length,
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="Onboarding Checklists" value={kpi.total} />
        <Stat label="In Progress" value={kpi.inProgress} accent={HRC.amber} />
        <Stat label="Complete" value={kpi.complete} accent={HRC.green} />
      </div>

      <Card>
        <SectionTitle>Joining Checklists</SectionTitle>
        {loading ? <Loading /> : rows.length === 0 ? <Empty>No checklists yet — they’re created automatically when a candidate is hired.</Empty> : (
          <Table>
            <thead>
              <tr>
                <Th>Employee</Th><Th>Designation</Th><Th>Department</Th>
                <Th>Progress</Th><Th>Status</Th><Th style={{ textAlign: 'right' }}></Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{r.full_name}</div>
                    <div style={{ fontSize: 11, color: HRC.navy, fontWeight: 700 }}>{r.employee_code || '—'}</div>
                  </Td>
                  <Td>{r.designation || '—'}</Td>
                  <Td>{r.department_name || '—'}</Td>
                  <Td><Progress done={+r.done} total={+r.total} /></Td>
                  <Td><Pill value={r.status} label={r.status === 'complete' ? 'Complete' : 'In Progress'} /></Td>
                  <Td style={{ textAlign: 'right' }}>
                    <Btn size="sm" variant="soft" onClick={() => setDetailEmp(r.employee_id)}>Open</Btn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {detailEmp && <ChecklistDetail employeeId={detailEmp} onClose={() => setDetailEmp(null)} onChanged={load} />}
    </div>
  );
}

function ChecklistDetail({ employeeId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/hr/checklists/${employeeId}`).then(r => setData(r.data.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const applyStatus = (s) => setData(d => ({ ...d, checklist: { ...d.checklist, status: s.status, completed_at: s.completed_at }, _done: +s.done, _total: +s.total }));

  const toggle = (item) => {
    const next = !item.is_done;
    setData(d => ({ ...d, items: d.items.map(i => i.id === item.id ? { ...i, is_done: next, done_at: next ? new Date().toISOString() : null } : i) }));
    api.patch(`/hr/checklist-items/${item.id}`, { is_done: next })
      .then(r => { applyStatus(r.data.data); onChanged?.(); })
      .catch(reportError('update this checklist item', load));
  };
  const saveNote = (item, note) => {
    if (note === (item.note || '')) return;
    setData(d => ({ ...d, items: d.items.map(i => i.id === item.id ? { ...i, note } : i) }));
    api.patch(`/hr/checklist-items/${item.id}`, { note }).catch(reportError('save this note', load));
  };

  const c = data?.checklist;
  const items = data?.items || [];
  const done = data?._done != null ? data._done : items.filter(i => i.is_done).length;
  const total = data?._total != null ? data._total : items.length;

  return (
    <Modal title="Joining Checklist" onClose={onClose} width={760}>
      {loading ? <Loading /> : !c ? <Empty>Could not load checklist.</Empty> : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 19, fontWeight: 800, color: HRC.text }}>{c.full_name}</div>
              <div style={{ fontSize: 12, color: HRC.muted, marginTop: 2 }}>
                <strong style={{ color: HRC.navy }}>{c.employee_code || '—'}</strong>
                {' · '}{c.department_name || '—'}{c.designation ? ` · ${c.designation}` : ''}
              </div>
              {c.status === 'complete' && c.completed_at && (
                <div style={{ fontSize: 11, color: HRC.green, fontWeight: 700, marginTop: 4 }}>✓ Completed {fmtDate(c.completed_at)}</div>
              )}
            </div>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
              <Pill value={c.status} label={c.status === 'complete' ? 'Complete' : 'In Progress'} />
              <Progress done={done} total={total} width={160} />
            </div>
          </div>

          {CATS.map(cat => {
            const list = items.filter(i => i.category === cat.key);
            if (!list.length) return null;
            const catDone = list.filter(i => i.is_done).length;
            return (
              <div key={cat.key} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: HRC.navy, textTransform: 'uppercase', letterSpacing: 0.5,
                  marginBottom: 8, paddingBottom: 6, borderBottom: `2px solid ${HRC.violet}`, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{cat.label}</span>
                  <span style={{ color: HRC.muted }}>{catDone}/{list.length}</span>
                </div>
                {list.map(item => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #F4F3F0' }}>
                    <input type="checkbox" checked={!!item.is_done} onChange={() => toggle(item)}
                      style={{ width: 17, height: 17, cursor: 'pointer', accentColor: HRC.green, flexShrink: 0 }} />
                    <span style={{ flex: '0 0 300px', fontSize: 13, color: item.is_done ? HRC.muted : HRC.text, textDecoration: item.is_done ? 'line-through' : 'none' }}>{item.label}</span>
                    <input
                      defaultValue={item.note || ''}
                      placeholder="note…"
                      onBlur={e => saveNote(item, e.target.value)}
                      style={{ ...input, flex: 1, padding: '5px 8px', fontSize: 12 }}
                    />
                  </div>
                ))}
              </div>
            );
          })}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" onClick={onClose}>Close</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}
